// Single-shot page-reload helper used to recover from "the page in this
// tab has drifted from the server" situations. The known triggers:
//
//   1. Vite chunk-load failure — a deploy rotated chunk hashes while this
//      tab was open (or, in dev, Vite's dep cache was invalidated under
//      a long-running dev server); the next dynamic import 404s/504s.
//      (See inertia.tsx.)
//   2. CSRF token mismatch on a JSON request — typically happens after a
//      re-login or session rotation: the open tab still has the old
//      meta-tag token, but the session cookie is fresh, so any non-GET
//      fails the forgery check. (See lib/api.ts.)
//
// Both cases share the same remedy (a full page load so the browser pulls
// fresh HTML + a fresh CSRF token), and the same hazard (a reload that
// doesn't fix the underlying problem would loop). The sessionStorage flag
// guards the loop — on the next successful Inertia navigation we clear it
// so a *later* drift can still trigger one more reload.
//
// When the drift is discovered mid-navigation (case 1), pass the URL the
// user was trying to reach. Inertia hasn't pushed history yet at that
// point, so a plain `location.reload()` would land them back on the page
// they were leaving and the click would look like a silent no-op.

const STALE_RELOAD_FLAG = 'app:stale-reload'

/**
 * Trigger a hard page load once per session. Navigates to `targetUrl`
 * when given (and different from the current URL), otherwise reloads the
 * current page. Returns `true` when a load was actually initiated,
 * `false` when the guard was already set (a previous reload in this
 * session didn't fix things). Callers should use the return value to
 * decide between "hang the promise — the page is going away" and
 * "surface the error normally so the user sees something instead of a
 * forever-loading screen."
 */
export function reloadForStaleState(targetUrl?: string | null): boolean {
  if (sessionStorage.getItem(STALE_RELOAD_FLAG)) return false
  sessionStorage.setItem(STALE_RELOAD_FLAG, '1')
  if (targetUrl && targetUrl !== window.location.href) {
    window.location.assign(targetUrl)
  } else {
    window.location.reload()
  }
  return true
}

export function clearStaleReloadGuard(): void {
  sessionStorage.removeItem(STALE_RELOAD_FLAG)
}
